/**
 * Centralized Consent Mode v2 emitter.
 *
 * Replays gtag consent commands and the `cookie_consent_update` GTM event so
 * GTM can rely on `cookie_consent_update` as a page-view replacement.
 *
 * - On hard reload: index.html already pushed `gtag('consent','default', denied)`.
 *   We only need `update` + `cookie_consent_update`.
 * - On SPA route change: no inline script runs, so we re-push `default` (always
 *   denied baseline) followed by `update` (mapped from stored prefs) and the
 *   `cookie_consent_update` event.
 * - On banner interaction: same behaviour as hard reload (default already there).
 */
import { supabase } from "@/integrations/supabase/client";
import {
  TRACKING_EVENTS,
  TRACKING_STORAGE_KEYS,
} from "@/config/tracking";

export interface CookiePreferences {
  functional: boolean;
  analytical: boolean;
  marketing: boolean;
}

export function readStoredConsent(): CookiePreferences | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(TRACKING_STORAGE_KEYS.PREFERENCES);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookiePreferences>;
    return {
      functional: !!parsed.functional,
      analytical: !!parsed.analytical,
      marketing: !!parsed.marketing,
    };
  } catch {
    return null;
  }
}

export function mapToGtagConsent(prefs: CookiePreferences) {
  return {
    analytics_storage: prefs.analytical ? "granted" : "denied",
    ad_storage: prefs.marketing ? "granted" : "denied",
    ad_user_data: prefs.marketing ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  } as const;
}

const DENIED_DEFAULT = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  functionality_storage: "granted",
  security_storage: "granted",
} as const;

interface EmitOptions {
  /** Whether to push a `gtag('consent','default', denied)` first.
   *  Use true for SPA route changes (no inline script ran). */
  includeDefault?: boolean;
  /** Optional prefs override (used by CookieConsent right after saving). */
  prefs?: CookiePreferences;
  /** Optional page path for the dataLayer event. */
  pagePath?: string;
}

export async function emitConsentSnapshot(options: EmitOptions = {}): Promise<void> {
  if (typeof window === "undefined") return;

  const prefs = options.prefs ?? readStoredConsent();
  if (!prefs) return; // no consent stored yet — banner will handle

  const w = window as any;
  w.dataLayer = w.dataLayer || [];

  // 1. Optional default (SPA route changes only — always denied baseline).
  if (options.includeDefault && typeof w.gtag === "function") {
    w.gtag("consent", "default", DENIED_DEFAULT);
  }

  // 2. Update — mapped from actual stored prefs.
  if (typeof w.gtag === "function") {
    w.gtag("consent", "update", mapToGtagConsent(prefs));
  }

  // 3. Best-effort logged-in email for GTM Data Layer Variable.
  let userEmail: string | null = null;
  try {
    const { data } = await supabase.auth.getSession();
    userEmail = data.session?.user?.email?.toLowerCase() ?? null;
  } catch {
    userEmail = null;
  }

  // 4. GTM custom event — page-view replacement.
  w.dataLayer.push({
    event: TRACKING_EVENTS.COOKIE_CONSENT_UPDATE,
    consent_functional: prefs.functional,
    consent_analytical: prefs.analytical,
    consent_marketing: prefs.marketing,
    email: userEmail,
    page_path: options.pagePath ?? (typeof location !== "undefined" ? location.pathname + location.search : null),
  });
}
